<p-drawer
  [visible]="!isHidden"
  styleClass="notifications-details-flyout"
  position="right"
  [maximizable]="true"
  [resizable]="true"
  [flyout]="true"
  [modal]="false"
  defaultWidth="640px"
  [baseZIndex]="1000"
  (onHide)="onClose()"
>
  <ng-template #actionbar>
    <p-toolbar styleClass="notif-drawer-actionbar">
      <p-button
        label="Cancel"
        size="small"
        [outlined]="true"
        severity="secondary"
        [disabled]="savePending"
        (click)="onCancelClick()"
        (keyup.enter)="onCancelClick()"
        (keyup.space)="onCancelClick()"
        aria-label="Cancel and close notification settings"
      />
      <p-button
        [label]="savePending ? 'Saving…' : 'Save'"
        size="small"
        [disabled]="!isSaveEnabled"
        (click)="onSave()"
        (keyup.enter)="onSave()"
        (keyup.space)="onSave()"
        aria-label="Save notification settings"
      />
    </p-toolbar>
  </ng-template>

  <ng-template pTemplate="content">
    <!-- Icon + name header -->
    <div class="notif-header">
      <div class="notif-header-icon">
        <i class="pi pi-cog"></i>
      </div>
      <div>
        <p class="notif-header-name">{{ row.name }}</p>
        <p class="notif-header-label">NAME</p>
      </div>
    </div>

    <h3 class="notif-section-title">Notification Settings</h3>

    <!-- Settings table -->
    <div class="notif-settings-table">
      <!-- Header row -->
      <div class="notif-settings-header">
        <span class="notif-col-name">NOTIFICATIONS</span>
        <span class="notif-col-frequency">FREQUENCY</span>
      </div>

      <!-- Setting rows — driven by the settings API -->
      @for (setting of notificationSettings; track trackByUiId($index, setting)) {
        <div class="notif-settings-row">
          <!-- Toggle -->
          <div class="notif-toggle-cell">
            <label class="notif-toggle-label" [attr.aria-label]="'Enable ' + setting.label">
              <input
                type="checkbox"
                [(ngModel)]="setting.enabled"
                [attr.aria-label]="'Enable ' + setting.label"
              />
              <span class="notif-toggle-track" [class.notif-toggle-track--on]="setting.enabled">
                <span
                  class="notif-toggle-thumb"
                  [class.notif-toggle-thumb--on]="setting.enabled"
                ></span>
              </span>
            </label>
          </div>
          <!-- Label -->
          <div class="notif-label-cell">
            <span
              class="notif-toggle-text"
              [class.notif-toggle-text--disabled]="!setting.enabled"
              >{{ setting.label }}</span
            >
          </div>
          <!-- Frequency -->
          <div class="notif-frequency-cell">
            @if (isNaFrequency(setting)) {
              <span class="notif-frequency-na">N/A</span>
            }
            @if (!isNaFrequency(setting)) {
              <select
                [(ngModel)]="setting.frequency"
                [disabled]="!setting.enabled"
                class="notif-frequency-select"
                [attr.aria-label]="setting.label + ' frequency'"
              >
                @for (opt of setting.allowed_frequencies; track opt) {
                  <option [value]="opt">
                    {{ opt }}
                  </option>
                }
              </select>
            }
          </div>
        </div>
      }
    </div>

    <!-- Recipients -->
    <div class="notif-recipients-header">
      <h4 class="notif-recipients-title">Notification Recipients*</h4>
      <p-button
        label="Add Recipient"
        icon="icon__actions-add is--medium is--blue"
        type="button"
        variant="text"
        size="small"
        [disabled]="isRecipientsLimitReached"
        (click)="addRecipientRow()"
        (keyup.enter)="addRecipientRow()"
        (keyup.space)="addRecipientRow()"
        [attr.aria-label]="
          isRecipientsLimitReached ? 'Maximum recipients reached' : 'Add recipient'
        "
      />
    </div>

    <div class="notif-recipients-list">
      @for (recipient of recipients; track recipient; let i = $index) {
        <div class="notif-recipient-item">
          <div
            class="notif-recipient-row"
            [class.notif-recipient-row--error]="editingRecipientIndex !== i && !!recipientErrors[i]"
          >
            <!-- View mode -->
            @if (editingRecipientIndex !== i) {
              <input
                type="email"
                [value]="recipient"
                readonly
                class="notif-recipient-input notif-recipient-input--readonly"
                [class.notif-recipient-input--error]="!!recipientErrors[i]"
                [attr.aria-label]="'Recipient email ' + (i + 1)"
                (click)="startEditRecipient(i)"
              />
              <p-button
                icon="pi pi-trash"
                [text]="true"
                severity="secondary"
                size="small"
                (click)="removeRecipient(i)"
                (keyup.enter)="removeRecipient(i)"
                (keyup.space)="removeRecipient(i)"
                [disabled]="recipients.length === 1"
                [attr.aria-label]="'Remove recipient ' + (i + 1)"
              />
            }
            <!-- Edit mode -->
            @if (editingRecipientIndex === i) {
              <input
                type="email"
                [(ngModel)]="editingRecipientEmail"
                class="notif-recipient-input"
                [class.notif-recipient-input--error]="!!editingRecipientEmailError"
                [attr.aria-label]="'Edit recipient email ' + (i + 1)"
                (keyup.enter)="confirmEditRecipient()"
                (keyup.escape)="cancelEditRecipient()"
              />
              <p-button
                icon="pi pi-check"
                [text]="true"
                size="small"
                (click)="confirmEditRecipient()"
                (keyup.enter)="confirmEditRecipient()"
                (keyup.space)="confirmEditRecipient()"
                aria-label="Confirm edit recipient"
              />
              <p-button
                icon="pi pi-times"
                [text]="true"
                severity="secondary"
                size="small"
                (click)="cancelEditRecipient()"
                (keyup.enter)="cancelEditRecipient()"
                (keyup.space)="cancelEditRecipient()"
                aria-label="Cancel edit recipient"
              />
            }
          </div>
          @if (editingRecipientIndex === i && editingRecipientEmailError) {
            <span class="notif-recipient-error" role="alert">{{ editingRecipientEmailError }}</span>
          }
          @if (editingRecipientIndex !== i && recipientErrors[i]) {
            <span class="notif-recipient-error" role="alert">{{ recipientErrors[i] }}</span>
          }
        </div>
      }

      <!-- New recipient input row -->
      @if (showNewRecipientInput && !isRecipientsLimitReached) {
        <div class="notif-recipient-item">
          <div
            class="notif-new-recipient-row"
            [class.notif-new-recipient-row--error]="!!recipientEmailError"
          >
            <input
              type="email"
              [(ngModel)]="recipientEmail"
              placeholder="Enter email address"
              aria-label="New recipient email"
              class="notif-recipient-input"
              (keyup.enter)="confirmNewRecipient()"
            />
            <p-button
              icon="pi pi-check"
              [text]="true"
              size="small"
              (click)="confirmNewRecipient()"
              (keyup.enter)="confirmNewRecipient()"
              (keyup.space)="confirmNewRecipient()"
              aria-label="Confirm add recipient"
            />
            <p-button
              icon="pi pi-times"
              [text]="true"
              severity="secondary"
              size="small"
              (click)="cancelNewRecipient()"
              (keyup.enter)="cancelNewRecipient()"
              (keyup.space)="cancelNewRecipient()"
              aria-label="Cancel add recipient"
            />
          </div>
          @if (recipientEmailError) {
            <span class="notif-recipient-error" role="alert">{{ recipientEmailError }}</span>
          }
        </div>
      }
    </div>

    @if (isRecipientsLimitReached) {
      <div class="notif-recipients-limit-wrap" role="status" aria-live="polite">
        <p-message
          severity="warn"
          text="You have reached the maximum recipients allowed."
          [closable]="false"
          styleClass="notif-recipients-limit-message"
        />
      </div>
    }
  </ng-template>
</p-drawer>

<p-confirmDialog appendTo="body" [baseZIndex]="1100"></p-confirmDialog>
